/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-09
 */

// Add third-party dependencies.
import { Box, Typography, Switch, Chip, useTheme } from '@mui/material';

/**
 * Props for the BillingToggle component.
 */
interface BillingToggleProps {
  /**
   * Whether annual billing is selected.
   */
  isAnnual: boolean;
  /**
   * Callback when the billing toggle changes.
   */
  onToggle: (isAnnual: boolean) => void;
}

/**
 * Billing toggle component that allows switching between monthly and annual billing.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The billing toggle component
 *
 * @example
 * return (
 *   <BillingToggle
 *     isAnnual={isAnnual}
 *     onToggle={setIsAnnual}
 *   />
 * );
 */
const BillingToggle: React.FC<BillingToggleProps> = ({
  isAnnual,
  onToggle,
}) => {
  const theme = useTheme();

  /**
   * Handle toggle change.
   */
  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onToggle(event.target.checked);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        borderRadius: 3,
        backgroundColor:
          theme.palette.mode === 'light' ? 'grey.50' : 'grey.900',
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Monthly Label */}
      <Typography
        variant="body1"
        sx={{
          fontWeight: isAnnual ? 400 : 600,
          color: isAnnual ? 'text.secondary' : 'text.primary',
          transition: 'all 0.3s ease',
        }}
      >
        Monthly
      </Typography>

      {/* Toggle Switch */}
      <Switch
        checked={isAnnual}
        onChange={handleToggleChange}
        color="primary"
        sx={{
          '& .MuiSwitch-thumb': {
            boxShadow: 2,
          },
        }}
        inputProps={{
          'aria-label': 'Switch between monthly and annual billing',
        }}
      />

      {/* Annual Label with Badge */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: isAnnual ? 600 : 400,
            color: isAnnual ? 'text.primary' : 'text.secondary',
            transition: 'all 0.3s ease',
          }}
        >
          Annual
        </Typography>

        {/* Savings Badge */}
        <Chip
          label="2 months free"
          size="small"
          color="success"
          variant={isAnnual ? 'filled' : 'outlined'}
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            height: 24,
            '& .MuiChip-label': {
              px: 1,
            },
            transition: 'all 0.3s ease',
            opacity: isAnnual ? 1 : 0.7,
          }}
        />
      </Box>
    </Box>
  );
};

export default BillingToggle;
